import React, { useContext, useEffect, useState } from 'react'
import {
  Box,
  Alert,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material'

import WebhookPortalContext from '../../services/webhookService.js'
import WhoAmIContext from '../../services/whoami'
import { ConfirmActionDialog } from '../../Components/Dialogs'

const EventTypeControls = ({ available, selected, onChange, disabled }) => (
  <FormGroup row>
    {available.map((eventType) => (
      <FormControlLabel
        key={eventType}
        control={(
          <Checkbox
            checked={selected.includes(eventType)}
            disabled={disabled}
            onChange={(event) => onChange(event.target.checked
              ? [...selected, eventType]
              : selected.filter((value) => value !== eventType))}
          />
        )}
        label={eventType}
      />
    ))}
  </FormGroup>
)

const PurposeSelect = ({ endpointId, purpose, valid = true, disabled, onChange, label = 'Traffic' }) => (
  <FormControl size="small" sx={{ minWidth: 190 }} disabled={disabled}>
    <InputLabel id={`webhook-purpose-${endpointId}`}>{label}</InputLabel>
    <Select
      labelId={`webhook-purpose-${endpointId}`}
      value={valid ? (purpose ?? '') : 'invalid'}
      label={label}
      onChange={(event) => onChange(event.target.value || null)}
    >
      {!valid && <MenuItem value="invalid" disabled>Existing filter needs review</MenuItem>}
      <MenuItem value="">All traffic</MenuItem>
      <MenuItem value="live">Live traffic</MenuItem>
      <MenuItem value="test">Test traffic</MenuItem>
    </Select>
  </FormControl>
)

const EndpointEditor = ({ endpoint, availableEventTypes, isAdmin, actions }) => {
  const [url, setUrl] = useState(endpoint.url)
  const [selectedEventTypes, setSelectedEventTypes] = useState(endpoint.eventTypes ?? [])
  const [disabled, setDisabled] = useState(endpoint.disabled === true)
  const [purpose, setPurpose] = useState(endpoint.purpose ?? null)
  const [purposeValid, setPurposeValid] = useState(endpoint.purposeConfigurationValid !== false)
  const [purposeUpdating, setPurposeUpdating] = useState(false)
  const [secret, setSecret] = useState(null)
  const [pendingAction, setPendingAction] = useState(null)
  const [pendingPurpose, setPendingPurpose] = useState(null)
  const [pendingRequest, setPendingRequest] = useState(null)
  const [actionError, setActionError] = useState(null)
  const requestPending = purposeUpdating || pendingRequest !== null

  useEffect(() => {
    setUrl(endpoint.url)
    setSelectedEventTypes(endpoint.eventTypes ?? [])
    setDisabled(endpoint.disabled === true)
    setPurpose(endpoint.purpose ?? null)
    setPurposeValid(endpoint.purposeConfigurationValid !== false)
    setSecret(null)
  }, [endpoint])

  const applyPurposeUpdate = async (nextPurpose) => {
    if (requestPending) return
    setPurpose(nextPurpose)
    setPurposeValid(true)
    setPurposeUpdating(true)
    try {
      await actions.setEndpointPurpose({ endpointId: endpoint.endpointId, purpose: nextPurpose })
    } catch {
      setPurpose(endpoint.purpose ?? null)
      setPurposeValid(endpoint.purposeConfigurationValid !== false)
      setActionError('Could not update webhook traffic. Reload the endpoint state before retrying.')
    } finally {
      setPurposeUpdating(false)
    }
  }

  const requestPurposeUpdate = (nextPurpose) => {
    if (requestPending || (purposeValid && nextPurpose === purpose)) return
    setPendingPurpose(nextPurpose)
    setPendingAction('change-purpose')
  }

  const runAction = async ({ name, action, failureMessage, onSuccess }) => {
    if (requestPending) return
    setPendingRequest(name)
    setActionError(null)
    try {
      const result = await action()
      if (onSuccess) onSuccess(result)
    } catch {
      setActionError(failureMessage)
    } finally {
      setPendingRequest(null)
    }
  }

  const confirmPendingAction = async () => {
    const action = pendingAction
    setPendingAction(null)
    if (action === 'change-purpose') {
      const nextPurpose = pendingPurpose
      setPendingPurpose(null)
      await applyPurposeUpdate(nextPurpose)
    }
    if (action === 'reveal-secret') {
      await runAction({
        name: action,
        action: () => actions.getEndpointSecret(endpoint.endpointId),
        failureMessage: 'Could not reveal the signing secret. Reload endpoint state before retrying.',
        onSuccess: setSecret,
      })
    }
    if (action === 'rotate-secret') {
      setSecret(null)
      await runAction({
        name: action,
        action: () => actions.rotateEndpointSecret(endpoint.endpointId),
        failureMessage: 'Signing-secret rotation may have completed. Reveal the current signing secret and reload endpoint state before retrying.',
        onSuccess: setSecret,
      })
    }
    if (action === 'delete-endpoint') {
      await runAction({
        name: action,
        action: () => actions.deleteEndpoint(endpoint.endpointId),
        failureMessage: 'Could not delete the webhook endpoint. Reload endpoint state before retrying.',
      })
    }
  }

  const cancelPendingAction = () => {
    setPendingAction(null)
    setPendingPurpose(null)
  }

  const purposeDescription = pendingPurpose === null
    ? `${endpoint.hasExistingRoutingFilter || !purposeValid ? "This will replace the endpoint's existing routing filter. " : ''}All traffic removes purpose isolation and delivers Live, Test, and unclassified traffic to this endpoint.`
    : `${endpoint.hasExistingRoutingFilter || !purposeValid ? "This will replace the endpoint's existing routing filter. " : ''}${pendingPurpose === 'live' ? 'Only Live traffic' : 'Only Test traffic'} will reach this endpoint. Historical, user-authenticated, internal, and legacy-key orders are unclassified; unclassified traffic will stop reaching this endpoint.`

  const confirmation = {
    'change-purpose': {
      title: 'Change webhook traffic?',
      description: purposeDescription,
      confirmLabel: 'Change traffic',
      destructive: false,
    },
    'reveal-secret': {
      title: 'Reveal signing secret?',
      description: 'The signing secret is a sensitive credential. Reveal it only in a private place and store it in an approved secrets manager.',
      confirmLabel: 'Reveal secret',
      destructive: false,
    },
    'rotate-secret': {
      title: 'Rotate signing secret?',
      description: 'Rotation invalidates the current signing secret immediately. Update the endpoint consumer with the new secret before processing another webhook.',
      confirmLabel: 'Rotate secret',
      destructive: true,
    },
    'delete-endpoint': {
      title: 'Delete webhook endpoint?',
      description: 'Deleting this endpoint stops future webhook delivery to its URL. This action cannot be undone.',
      confirmLabel: 'Delete endpoint',
      destructive: true,
    },
  }[pendingAction] ?? {
    title: '',
    description: '',
    confirmLabel: '',
    destructive: false,
  }

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      {actionError && <Alert severity="error">{actionError}</Alert>}
      <TextField label="Endpoint URL" size="small" value={url} disabled={!isAdmin || requestPending} onChange={(event) => setUrl(event.target.value)} />
      <Typography variant="subtitle2">Event types</Typography>
      <EventTypeControls available={availableEventTypes} selected={selectedEventTypes} onChange={setSelectedEventTypes} disabled={!isAdmin || requestPending} />
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
        <PurposeSelect
          endpointId={endpoint.endpointId}
          purpose={purpose}
          valid={purposeValid}
          disabled={!isAdmin || requestPending}
          onChange={requestPurposeUpdate}
        />
        <FormControlLabel
          control={<Switch checked={disabled} disabled={!isAdmin || requestPending} onChange={(event) => setDisabled(event.target.checked)} />}
          label="Endpoint disabled"
        />
        <Button
          onClick={() => runAction({
            name: 'update-endpoint',
            action: () => actions.updateEndpoint({
              endpointId: endpoint.endpointId,
              url,
              eventTypes: selectedEventTypes,
              disabled,
            }),
            failureMessage: 'Could not update the webhook endpoint. Reload endpoint state before retrying.',
          })}
          disabled={!isAdmin || !purposeValid || requestPending || !url || selectedEventTypes.length === 0}
        >
          Save endpoint
        </Button>
        <Button onClick={() => setPendingAction('delete-endpoint')} disabled={!isAdmin || requestPending} color="error">Delete endpoint</Button>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
        <Button onClick={() => setPendingAction('reveal-secret')} disabled={!isAdmin || requestPending}>Show signing secret</Button>
        <Button onClick={() => setPendingAction('rotate-secret')} disabled={!isAdmin || requestPending}>Rotate signing secret</Button>
        {secret && <Typography component="code" sx={{ overflowWrap: 'anywhere' }}>{secret}</Typography>}
      </Box>
      <ConfirmActionDialog
        open={pendingAction !== null}
        title={confirmation.title}
        description={confirmation.description}
        confirmLabel={confirmation.confirmLabel}
        destructive={confirmation.destructive}
        onCancel={cancelPendingAction}
        onConfirm={confirmPendingAction}
      />
    </Box>
  )
}

const NewEndpoint = ({ availableEventTypes, isAdmin, createEndpoint }) => {
  const [url, setUrl] = useState('')
  const [selectedEventTypes, setSelectedEventTypes] = useState([])
  const [purpose, setPurpose] = useState(null)
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState(null)

  const create = async () => {
    if (creating) return
    setCreating(true)
    setError(null)
    try {
      await createEndpoint({ url, eventTypes: selectedEventTypes, purpose })
      setUrl('')
      setSelectedEventTypes([])
      setPurpose(null)
    } catch {
      setError('Could not create the webhook endpoint. Reload endpoint state before retrying.')
    } finally {
      setCreating(false)
    }
  }

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      <Typography variant="h6">Add endpoint</Typography>
      {error && <Alert severity="error">{error}</Alert>}
      <TextField label="New endpoint URL" size="small" value={url} disabled={!isAdmin || creating} onChange={(event) => setUrl(event.target.value)} />
      <EventTypeControls available={availableEventTypes} selected={selectedEventTypes} onChange={setSelectedEventTypes} disabled={!isAdmin || creating} />
      <Box sx={{ display: 'flex', gap: '0.75rem' }}>
        <PurposeSelect endpointId="new" purpose={purpose} disabled={!isAdmin || creating} onChange={setPurpose} label="New endpoint traffic" />
        <Button onClick={create} disabled={!isAdmin || creating || !url || selectedEventTypes.length === 0}>Create endpoint</Button>
      </Box>
    </Box>
  )
}

const Webhooks = () => {
  const actions = useContext(WebhookPortalContext)
  const { role } = useContext(WhoAmIContext)
  const isAdmin = role === 'ADMIN'
  const configurationLoading = actions.endpointsLoading === true || actions.eventTypesLoading === true
  const configurationError = actions.endpointsError || actions.eventTypesError
  const authoringEnabled = isAdmin && !configurationLoading && !configurationError

  const retryConfigurationLoad = () => {
    actions.loadEndpoints?.().catch(() => {})
    actions.loadEventTypes?.().catch(() => {})
  }

  return (
    <Box sx={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <Typography variant="h5">Webhook traffic</Typography>
      <Typography>
        Select Live traffic or Test traffic to isolate an endpoint. All traffic preserves the existing unfiltered behavior.
      </Typography>
      {configurationLoading && <Alert severity="info">Loading webhook configuration.</Alert>}
      {configurationError && (
        <Alert severity="error" action={<Button color="inherit" onClick={retryConfigurationLoad}>Retry</Button>}>
          Webhook configuration could not be loaded. Existing data is shown read-only until a retry succeeds.
        </Alert>
      )}
      {actions.endpoints.map((endpoint) => (
        <EndpointEditor
          key={endpoint.endpointId}
          endpoint={endpoint}
          availableEventTypes={actions.eventTypes}
          isAdmin={authoringEnabled}
          actions={actions}
        />
      ))}
      <NewEndpoint availableEventTypes={actions.eventTypes} isAdmin={authoringEnabled} createEndpoint={actions.createEndpoint} />
    </Box>
  )
}

export default Webhooks
